import React, { useState, useEffect, useRef } from 'react';
import { Search, ShoppingCart, Menu, X, MessageSquare, Package, TrendingUp, 
         Zap, Server, Cpu, Wind, Battery, Network, ChevronRight, 
         Star, Filter, DollarSign, Truck, Warehouse, Send, Bot, User,
         BarChart3, Users, Settings, Bell, Home, FileText, Award } from 'lucide-react';

// AI Agent configs
const agentConfigs = {
  shopping: { name: 'Shopping Assistant', icon: <Bot className="w-5 h-5" /> },
  vendor: { name: 'Vendor Liaison', icon: <Users className="w-5 h-5" /> },
  support: { name: 'Technical Support', icon: <MessageSquare className="w-5 h-5" /> },
  inventory: { name: 'Inventory Optimizer', icon: <Package className="w-5 h-5" /> },
  recommendations: { name: 'Product Recommender', icon: <TrendingUp className="w-5 h-5" /> }
};

const AIAgentSystem = ({ agentType = 'shopping' }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => scrollToBottom(), [messages]);

  const config = agentConfigs[agentType] || agentConfigs.shopping;

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    const userInput = input.toLowerCase();
    setInput('');
    setIsLoading(true);

    setTimeout(() => {
      let response = '';
      if (agentType === 'shopping') {
        if (userInput.includes('switch') || userInput.includes('network')) {
          response = "For 100-rack datacenter:\n• Cisco Nexus 9300: $24,500\n• Arista 7280R3: $89,000\nTotal ~$3.2M, 2-3 weeks lead time";
        } else if (userInput.includes('gpu')) {
          response = "AI GPUs:\n• NVIDIA H100 80GB: $32,000 (4-6 weeks)\n• AMD MI300X 192GB: $28,000 (3-5 days)";
        } else {
          response = "I can help with:\n🔷 Networking\n💚 GPUs  \n⚡ Power\n❄️ Cooling\nWhat do you need?";
        }
      } else if (agentType === 'vendor') {
        response = "Vendor status:\n• NVIDIA: 4-6 weeks\n• Cisco: 2-3 days\n• Schneider: Net-30 terms";
      } else if (agentType === 'support') {
        response = "Technical help:\n• Power design\n• Network architecture\n• Cooling calculations";
      } else if (agentType === 'inventory') {
        response = "Low stock:\n• H100: 3 units\n• UPS: 5 units\nRecommend $1.2M reorder";
      } else {
        response = "Complete build:\n• Arista networking\n• NVIDIA GPUs\n• Schneider power\nBudget: $5-8M";
      }
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      setIsLoading(false);
    }, 800);
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 rounded-lg border border-cyan-500/20">
      <div className="px-4 py-3 border-b border-cyan-500/20 flex items-center gap-2 bg-gradient-to-r from-cyan-950 to-slate-900">
        {config.icon}
        <span className="font-semibold text-cyan-400">{config.name}</span>
        <div className="ml-auto w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 mt-8">
            <div className="text-4xl mb-2">🤖</div>
            <p>Hi! I'm your {config.name}.</p>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-white" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
              msg.role === 'user' ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white' : 'bg-slate-800 text-slate-200 border border-cyan-500/20'
            }`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Bot className="w-4 h-4 text-white animate-pulse" />
            </div>
            <div className="bg-slate-800 rounded-lg px-4 py-2 border border-cyan-500/20">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="p-4 border-t border-cyan-500/20">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 bg-slate-800 border border-cyan-500/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
          <button
            onClick={handleSendMessage}
            disabled={isLoading || !input.trim()}
            className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const products = [
  { id: 1, name: 'Cisco Nexus 9300-FX3', category: 'networking', price: 24500, image: '🔷', specs: '48x 10/25G SFP28', stock: 'In Stock', fulfillment: 'stocking', vendor: 'Cisco', leadTime: '2-3 days', rating: 4.8 },
  { id: 2, name: 'NVIDIA H100 80GB GPU', category: 'gpu', price: 32000, image: '🟢', specs: 'PCIe 5.0, 80GB HBM3', stock: 'Limited', fulfillment: 'dropship', vendor: 'NVIDIA', leadTime: '4-6 weeks', rating: 5.0 },
  { id: 3, name: 'Schneider APC UPS', category: 'power', price: 67000, image: '⚡', specs: '100kW, N+1 Redundancy', stock: 'In Stock', fulfillment: 'stocking', vendor: 'Schneider', leadTime: '1 week', rating: 4.9 },
  { id: 4, name: 'Vertiv Cooling Unit', category: 'cooling', price: 45000, image: '❄️', specs: '400kW Variable Speed', stock: 'Pre-Order', fulfillment: 'dropship', vendor: 'Vertiv', leadTime: '8-10 weeks', rating: 4.7 },
  { id: 5, name: 'Arista 7280R3 400G', category: 'networking', price: 89000, image: '🔶', specs: '32x 400G QSFP-DD', stock: 'In Stock', fulfillment: 'stocking', vendor: 'Arista', leadTime: '1-2 weeks', rating: 4.9 },
  { id: 6, name: 'AMD MI300X 192GB', category: 'gpu', price: 28000, image: '🔴', specs: '192GB HBM3, PCIe 5.0', stock: 'In Stock', fulfillment: 'stocking', vendor: 'AMD', leadTime: '3-5 days', rating: 4.6 }
];

const categories = [
  { id: 'all', name: 'All Products', icon: <Server />, count: 247 },
  { id: 'networking', name: 'Networking', icon: <Network />, count: 89 },
  { id: 'gpu', name: 'GPUs', icon: <Cpu />, count: 56 },
  { id: 'power', name: 'Power', icon: <Battery />, count: 43 },
  { id: 'cooling', name: 'Cooling', icon: <Wind />, count: 59 }
];

const ProductCatalog = ({ currentView, setCurrentView }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAIAssistant, setShowAIAssistant] = useState(false);

  const filteredProducts = products.filter(p => {
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <header className="border-b border-cyan-500/20 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between">
            {/* LEFT: Logo + Store/Admin Toggle */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="text-lg">⚡</div>
                <div>
                  <h1 className="text-base font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Layeready</h1>
                  <p className="text-[10px] text-slate-400 leading-none">Data Center</p>
                </div>
              </div>
              
              <div className="flex items-center gap-1 bg-slate-800/70 rounded-md p-0.5">
                <button
                  onClick={() => setCurrentView('catalog')}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${
                    currentView === 'catalog' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Store
                </button>
                <button
                  onClick={() => setCurrentView('admin')}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${
                    currentView === 'admin' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Admin
                </button>
              </div>
            </div>

            {/* RIGHT: AI + Cart */}
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowAIAssistant(!showAIAssistant)}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-cyan-600 text-white rounded-md hover:bg-cyan-500 text-[11px] font-medium"
              >
                <Bot className="w-3.5 h-3.5" />
                <span>AI</span>
              </button>
              
              <button className="relative p-1.5 text-slate-400 hover:text-cyan-400">
                <ShoppingCart className="w-4 h-4" />
                <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-cyan-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">3</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-slate-900 rounded-lg p-4 border border-cyan-500/20">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-cyan-500/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>

            <div className="bg-slate-900 rounded-lg p-4 border border-cyan-500/20">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Filter className="w-4 h-4 text-cyan-400" />
                Categories
              </h3>
              <div className="space-y-1.5">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all text-xs ${
                      selectedCategory === cat.id ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    <div className="w-4 h-4">{cat.icon}</div>
                    <span className="flex-1 text-left">{cat.name}</span>
                    <span className="text-[10px] opacity-70">{cat.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <h2 className="text-xl font-bold text-white">{filteredProducts.length} Products</h2>

            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {filteredProducts.map(product => (
                <div key={product.id} className="bg-slate-900 rounded-lg border border-cyan-500/20 overflow-hidden hover:border-cyan-500/50 transition-all group">
                  <div className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center text-5xl">
                    {product.image}
                  </div>
                  
                  <div className="p-3 space-y-2">
                    <h3 className="font-semibold text-sm text-white group-hover:text-cyan-400">{product.name}</h3>
                    <p className="text-xs text-slate-400">{product.specs}</p>

                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3 h-3 ${i < Math.floor(product.rating) ? 'fill-current' : ''}`} />
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-xl font-bold text-cyan-400">${product.price.toLocaleString()}</div>
                      <div className={`text-[10px] px-2 py-0.5 rounded-full ${
                        product.stock === 'In Stock' ? 'bg-green-500/20 text-green-400' : product.stock === 'Limited' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {product.stock}
                      </div>
                    </div>

                    <button className="w-full py-1.5 bg-cyan-600 text-white rounded-md hover:bg-cyan-500 text-xs font-medium flex items-center justify-center gap-1.5">
                      <ShoppingCart className="w-3.5 h-3.5" />
                      Add to Quote
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showAIAssistant && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-lg border border-cyan-500/20 w-full max-w-2xl h-[600px] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-cyan-500/20">
              <h2 className="text-lg font-bold text-white">AI Shopping Assistant</h2>
              <button onClick={() => setShowAIAssistant(false)} className="p-2 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1"><AIAgentSystem agentType="shopping" /></div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = ({ currentView, setCurrentView }) => {
  const stats = [
    { label: 'Revenue', value: '$2.4M', change: '+12.5%', icon: <DollarSign />, color: 'green' },
    { label: 'Orders', value: '147', change: '+8.2%', icon: <Package />, color: 'blue' },
    { label: 'Vendors', value: '34', change: '+3', icon: <Users />, color: 'purple' },
    { label: 'Products', value: '247', change: '+15', icon: <Server />, color: 'cyan' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <header className="border-b border-cyan-500/20 bg-slate-900/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between">
            {/* LEFT: Logo + Store/Admin */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="text-lg">⚡</div>
                <div>
                  <h1 className="text-base font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Layeready</h1>
                  <p className="text-[10px] text-slate-400 leading-none">Admin Dashboard</p>
                </div>
              </div>
              
              <div className="flex items-center gap-1 bg-slate-800/70 rounded-md p-0.5">
                <button
                  onClick={() => setCurrentView('catalog')}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${
                    currentView === 'catalog' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Store
                </button>
                <button
                  onClick={() => setCurrentView('admin')}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${
                    currentView === 'admin' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Admin
                </button>
              </div>
            </div>

            {/* RIGHT: Notifications + Profile */}
            <div className="flex items-center gap-2">
              <button className="relative p-1.5 text-slate-400 hover:text-cyan-400">
                <Bell className="w-4 h-4" />
                <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">5</span>
              </button>
              <button className="flex items-center gap-1.5 px-2 py-1 bg-slate-800 rounded-md hover:bg-slate-700">
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600"></div>
                <span className="text-white text-xs">Admin</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-slate-900 rounded-lg p-4 border border-cyan-500/20">
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 rounded-lg bg-${stat.color}-500/20 text-${stat.color}-400`}>{stat.icon}</div>
                <span className={`text-sm ${stat.change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>{stat.change}</span>
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-sm text-slate-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LayereadyPlatform = () => {
  const [currentView, setCurrentView] = useState('catalog');
  return (
    <div className="min-h-screen bg-slate-950">
      {currentView === 'catalog' ? (
        <ProductCatalog currentView={currentView} setCurrentView={setCurrentView} />
      ) : (
        <AdminDashboard currentView={currentView} setCurrentView={setCurrentView} />
      )}
    </div>
  );
};

export default LayereadyPlatform;
